import React, { Component } from 'react';
import { Spin } from 'antd';
import Skeleton from 'react-loading-skeleton';
import { withRouter } from 'react-router-dom';
import { DSIcon } from '../common/atoms';
import Button from '../common/atoms/DSButton';
import { SALES_ROLE } from '../../constants/overview';

const SectionLabel = ({ children }) => (
  <div className='mb-ds-2 text-ts-xs font-semibold uppercase tracking-[0.1em] text-gray-500'>{children}</div>
);

const FieldValue = ({ children, className = '' }) => (
  <div className={`text-ts-md leading-relaxed text-gray-700 ${className}`}>{children}</div>
);

const StatTile = ({ label, value, tone }) => {
  const toneClass = {
    going: 'border-ds-green-200 bg-ds-green-lt text-ds-green-dk',
    notGoing: 'border-gray-300 bg-gray-100 text-gray-700',
    notSure: 'border-ds-amber-200 bg-ds-amber-lt text-ds-amber-dk',
  }[tone];
  return (
    <div className={`flex flex-1 flex-col gap-ds-1 rounded-ds-lg border px-ds-4 py-ds-3 ${toneClass}`}>
      <span className='text-ts-xs font-semibold uppercase tracking-[0.08em] opacity-80'>{label}</span>
      <span className='font-jetbrains-mono text-ts-2xl font-semibold tabular-nums leading-none'>{value}</span>
    </div>
  );
};

const PollOption = ({ name, count }) => (
  <div className='flex items-center justify-between rounded-ds-md border border-gray-300 bg-white px-ds-3 py-ds-2 text-ts-sm'>
    <span className='text-gray-700'>{name}</span>
    <span className='font-jetbrains-mono text-ts-sm font-semibold tabular-nums text-gray-900'>{count}</span>
  </div>
);

const ContactRow = ({ icon, value }) => (
  value ? (
    <div className='flex items-center gap-ds-2 text-ts-sm text-gray-700'>
      <DSIcon name={icon} className='text-ds-brand shrink-0' size={16} />
      <span className='break-all'>{value}</span>
    </div>
  ) : null
);

class KnowMoreDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      redirect: false,
      data: [],
      isLoading: true,
    };
  }

  componentDidMount() {
    this.getKnowMoreData();
  }

  getKnowMoreData() {
    let that = this;
    let id = this.props.eventId;
    window.axios.get(window.API_URL + 'corporate/events/' + id)
      .then(function (response) {
        that.setState({ data: response.data, isLoading: false });
      })
      .catch(function (error) {
        console.log(error);
      });
  }

  goBack() {
    this.setState({ redirect: true });
  }

  deactivateEvent() {
    let that = this;
    let id = this.props.eventId;
    window.axios.put(window.API_URL + 'corporate/events/' + id + '/deactivate')
      .then(function (response) {
        that.setState({ redirect: true, isLoading: false });
        that.props.postUpdate();
      })
      .catch(function (error) {
        console.log(error);
      });
  }

  render() {
    if (this.state.isLoading) {
      return (
        <div className='flex flex-col gap-ds-4 p-ds-6'>
          <Skeleton height={28} width='40%' />
          <Skeleton height={200} />
          <Skeleton count={3} height={16} />
          <Skeleton height={80} />
          <div className='flex items-center justify-center pt-ds-4'>
            <Spin size='large' />
          </div>
        </div>
      );
    }

    const data = this.state.data;
    const going = data.employees?.going ?? 0;
    const not_going = data.employees?.not_going ?? 0;
    const not_sure = data.employees?.not_sure ?? 0;
    const event_options = data.event_options ?? [];
    const event_contacts = data.event_contacts ?? [];
    const event_status = data.event_status === 'active';
    const show_footer = (data.event_type === 'default_events' || data.sponsor === null) ? false : data.completed_or_not;
    const isFree = !data.price || parseFloat(data.price) === 0;

    return (
      <div className='flex h-full flex-col bg-white'>
        <div className='flex-1 overflow-y-auto'>
          <div className='space-y-ds-7 px-ds-2 py-ds-6'>
            <header className='flex flex-wrap items-center justify-between gap-ds-3'>
              <h2 className='mb-0 text-ts-xl font-fig-semibold text-gray-900'>Event Preview</h2>
              {event_status && (
                <span className='inline-flex items-center gap-1.5 rounded-ds-pill bg-ds-green-lt px-ds-3 py-1 text-ts-xs font-semibold text-ds-green-dk ring-1 ring-ds-green-200'>
                  <span className='h-1.5 w-1.5 rounded-full bg-ds-green'></span>
                  Active
                </span>
              )}
            </header>

            {data.image_url && (
              <div className='overflow-hidden rounded-ds-lg border border-gray-300 bg-gray-100'>
                <img alt='event' src={data.image_url} className='block h-auto w-full object-cover' />
              </div>
            )}

            <section>
              <SectionLabel>Event Title</SectionLabel>
              <FieldValue className='text-ts-lg font-semibold text-gray-900'>{data.name}</FieldValue>
            </section>

            <section>
              <SectionLabel>Date and Time</SectionLabel>
              <div className='grid grid-cols-1 gap-ds-3 sm:grid-cols-2'>
                <div className='rounded-ds-lg border border-gray-300 bg-gray-100 px-ds-4 py-ds-3'>
                  <div className='text-ts-xs font-semibold uppercase tracking-[0.08em] text-gray-500'>Starts from</div>
                  <div className='mt-ds-1 font-jetbrains-mono text-ts-sm font-semibold tabular-nums text-gray-900'>{data.start_time}</div>
                </div>
                <div className='rounded-ds-lg border border-gray-300 bg-gray-100 px-ds-4 py-ds-3'>
                  <div className='text-ts-xs font-semibold uppercase tracking-[0.08em] text-gray-500'>Ends on</div>
                  <div className='mt-ds-1 font-jetbrains-mono text-ts-sm font-semibold tabular-nums text-gray-900'>{data.end_time}</div>
                </div>
              </div>
            </section>

            <section>
              <SectionLabel>Entry Fee</SectionLabel>
              {isFree ? (
                <span className='inline-flex items-center rounded-ds-pill bg-ds-brand-lt px-ds-3 py-1 text-ts-xs font-semibold text-ds-brand ring-1 ring-ds-brand-200'>Free Entry</span>
              ) : (
                <FieldValue className='font-jetbrains-mono text-ts-lg font-semibold tabular-nums text-gray-900'>{data.price}</FieldValue>
              )}
            </section>

            {data.description && (
              <section>
                <SectionLabel>Event Description</SectionLabel>
                <FieldValue>
                  <div className='prose-sm leading-relaxed' dangerouslySetInnerHTML={{ __html: data.description }} />
                </FieldValue>
              </section>
            )}

            {data.event_address && (
              <section>
                <SectionLabel>Event Address</SectionLabel>
                <FieldValue>{data.event_address}</FieldValue>
              </section>
            )}

            {data.event_type !== 'default_events' && (
              <section>
                <SectionLabel>Employees RSVP</SectionLabel>
                <div className='flex flex-col gap-ds-3 sm:flex-row'>
                  <StatTile label='Going' value={going} tone='going' />
                  <StatTile label='Not going' value={not_going} tone='notGoing' />
                  <StatTile label='Not sure' value={not_sure} tone='notSure' />
                </div>
              </section>
            )}

            {event_options.length > 0 && (
              <section>
                <SectionLabel>Poll Result</SectionLabel>
                <div className='space-y-ds-4'>
                  {event_options.map((object, i) => (
                    <div key={i} className='rounded-ds-lg border border-gray-300 bg-white p-ds-4'>
                      <div className='mb-ds-3 text-ts-sm font-semibold text-gray-900'>
                        <span className='mr-ds-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-ds-brand-lt text-ts-xs font-semibold text-ds-brand'>{i + 1}</span>
                        {object.name}
                      </div>
                      <div className='grid grid-cols-1 gap-ds-2 sm:grid-cols-2'>
                        {(object.options || []).map((obj, j) => (
                          <PollOption key={j} name={obj.name} count={obj.count} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {event_contacts.length > 0 && (
              <section>
                <SectionLabel>Contact Details</SectionLabel>
                <div className='space-y-ds-4'>
                  {event_contacts.map((object, i) => (
                    <div key={i} className='rounded-ds-lg border border-gray-300 bg-white p-ds-4 space-y-ds-2'>
                      <ContactRow icon='user' value={object.name} />
                      <ContactRow icon='phone' value={object.mobile} />
                      <ContactRow icon='mail' value={object.email} />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        {show_footer && (
          <div className='sticky bottom-0 flex shrink-0 items-center justify-end gap-ds-3 border-t border-gray-300 bg-white px-ds-6 py-ds-4 shadow-[0_-4px_12px_rgba(15,17,21,.04)]'>
            {event_status ? (
              <Button type='danger' onClick={this.deactivateEvent.bind(this)} disabled={SALES_ROLE === 'sales'}>Unpublish</Button>
            ) : (
              <Button onClick={this.deactivateEvent.bind(this)}>Publish</Button>
            )}
            <Button
              type='primary'
              disabled={SALES_ROLE === 'sales'}
              onClick={() => this.props.history.push('/wellness-calendar/event/' + data.id + '/edit')}
            >
              Edit
            </Button>
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(KnowMoreDrawer);
